import { orderBy } from 'lodash';

export interface RealTimeMachines {
  id: number;
  companyId: number;
  description: null;
  machineRef: string;
  status: string;
  title: string;
  hasAlert: boolean;
}

export interface RealtimeMachineStatus {
  machineId: number;
  start: Date;
  end: Date;
  hasAlert: boolean;
  hasShift: boolean;
  isOutOfShift: boolean;
  label: string;
  status: string;
  title: string;
}

export enum RealtimeMachineStatusEnum {
  stoped = 'S',
  working = 'W',
  noData = 'N',
  noShift = 'NO_SHIFT'
}

export enum MachineStatusEnum {
  stoped = 'STOPED',
  working = 'WORKING',
  noData = 'NO_DATA',
  noShift = 'NO_SHIFT'
}

export enum TimeRangeEnum {
  yesterday = 'YESTERDAY',
  today = 'TODAY',
  week = 'WEEK'
}

interface AvailabilityMetric {
  availabilityIndicator: number;
  shiftMinutes: number;
  workingMinutes: number;
}

interface MachineAvailabilityMetric extends AvailabilityMetric {
  machineId: number;
  machineTitle: string;
}

export interface MachineWorkingReport {
  compare: AvailabilityMetric;
  current: AvailabilityMetric;
  machines: MachineAvailabilityMetric[];
}

export function getRealtimeStatusFromMachines(
  machines: RealtimeMachineStatus[]
) {
  const stopedMachines = machines.filter(
    (x) => x.hasShift && x.status === RealtimeMachineStatusEnum.stoped
  );
  const workingMachines = machines.filter(
    (x) =>
      x.hasShift &&
      x.status === RealtimeMachineStatusEnum.working &&
      !x.isOutOfShift
  );
  const noDataMachines = machines.filter(
    (x) =>
      x.hasShift &&
      !x.isOutOfShift &&
      x.status === RealtimeMachineStatusEnum.noData
  );

  return {
    stopedMachines: stopedMachines.length,
    workingMachines: workingMachines.length,
    noDataMachines: noDataMachines.length,
    noShiftMachines: machines.filter((x) => !x.hasShift || x.isOutOfShift)
      .length,
    machines: machines
  };
}

export function convertToMachineWorkingReport(
  report: any
): MachineWorkingReport {
  return {
    compare: {
      ...report.compare,
      shiftMinutes: parseInt(report.compare.shiftMinutes),
      workingMinutes: parseInt(report.compare.workingMinutes)
    },
    current: {
      ...report.current,
      shiftMinutes: parseInt(report.current.shiftMinutes),
      workingMinutes: parseInt(report.current.workingMinutes)
    },
    machines: report.machines.map((x) => ({
      ...x,
      shiftMinutes: parseInt(x.shiftMinutes),
      workingMinutes: parseInt(x.workingMinutes)
    }))
  };
}

export function calculateDataForGeneralIndicators(
  report: MachineWorkingReport
) {
  const availability = getPercentage(report.current.availabilityIndicator);
  const previusPeriodAvailability = getPercentage(
    report.compare.availabilityIndicator
  );
  const trend = getPercentage(
    (availability - previusPeriodAvailability) / previusPeriodAvailability
  );
  const highAvailabilityMachines = [...report.machines].sort(
    (m, n) => n.availabilityIndicator - m.availabilityIndicator
  );
  const lowAvailabilityMachines = [...report.machines].sort(
    (m, n) => m.availabilityIndicator - n.availabilityIndicator
  );
  const sortedMachines = orderBy(
    report.machines,
    'availabilityIndicator',
    'desc'
  );
  const machines = getFormatMachineForListing(sortedMachines, false);

  return {
    availability,
    trend,
    highAvailability: getFormatMachineForListing(
      highAvailabilityMachines,
      false
    ),
    lowAvailability: getFormatMachineForListing(lowAvailabilityMachines, true),
    machines
  };
}
const getPercentage = (value: number) =>
  parseFloat(parseFloat(`${value * 100}`).toFixed(0));

const getFormatMachineForListing = (
  machines: MachineAvailabilityMetric[],
  isNegative: boolean
) =>
  machines.map((m) => ({
    name: m.machineTitle,
    id: m.machineId,
    isNegative,
    value: `${getPercentage(m.availabilityIndicator)}%`
  }));
